import React, { useState, useEffect } from "react";
import axios from "axios";
import { Upload, Image as ImageIcon, Trash2 } from "lucide-react";
import { Card, Button, Badge, Spinner, Input, Select, Textarea, FileUploadInput } from "../../shared/ui";
import { useAuth } from "../../App";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const JobUploadsSection = ({ jobId, jobTitle }) => {
  const { user } = useAuth();
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [newUpload, setNewUpload] = useState({
    title: "",
    description: "",
    upload_type: "progress_photo",
    file_url: ""
  });

  useEffect(() => {
    fetchUploads();
  }, [jobId]);

  const fetchUploads = async () => {
    try {
      const res = await axios.get(`${API}/jobs/${jobId}/uploads`);
      setUploads(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newUpload.file_url) {
      alert("Please enter a file/photo URL");
      return;
    }
    try {
      await axios.post(`${API}/jobs/${jobId}/uploads`, newUpload);
      setNewUpload({ title: "", description: "", upload_type: "progress_photo", file_url: "" });
      setShowForm(false);
      fetchUploads();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to upload");
    }
  };

  const handleDelete = async (uploadId) => {
    if (!window.confirm("Delete this upload?")) return;
    try {
      await axios.delete(`${API}/jobs/${jobId}/uploads/${uploadId}`);
      fetchUploads();
    } catch (e) {
      alert("Failed to delete");
    }
  };

  const uploadTypeLabels = {
    progress_photo: "Progress Photo",
    document_photo: "Document Photo",
    incident_photo: "Incident Photo",
    timesheet_photo: "Timesheet",
    report_photo: "Report Photo",
    evidence: "Evidence",
    other: "Other"
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="font-bold uppercase">Site Photos & Documents</h4>
        <Button size="sm" onClick={() => setShowForm(!showForm)}>
          <Upload className="w-4 h-4 mr-1" /> Upload
        </Button>
      </div>

      {showForm && (
        <Card className="bg-slate-50">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <Input
                label="Title"
                value={newUpload.title}
                onChange={(e) => setNewUpload({...newUpload, title: e.target.value})}
                required
              />
              <Select
                label="Type"
                value={newUpload.upload_type}
                onChange={(e) => setNewUpload({...newUpload, upload_type: e.target.value})}
                options={Object.entries(uploadTypeLabels).map(([v, l]) => ({ value: v, label: l }))}
              />
            </div>
            <FileUploadInput
              label="Upload File"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif"
              onFileSelect={(fileUrl, fileName) => setNewUpload({...newUpload, file_url: fileUrl})}
              currentFile={newUpload.file_url ? "File uploaded" : null}
              onClear={() => setNewUpload({...newUpload, file_url: ""})}
            />
            <Textarea
              label="Description"
              value={newUpload.description}
              onChange={(e) => setNewUpload({...newUpload, description: e.target.value})}
            />
            <div className="flex gap-2">
              <Button type="submit" size="sm" disabled={!newUpload.file_url}>Save</Button>
              <Button type="button" variant="ghost" size="sm" onClick={() => setShowForm(false)}>Cancel</Button>
            </div>
          </form>
        </Card>
      )}

      {loading ? (
        <div className="flex justify-center py-8"><Spinner /></div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {uploads.map(upload => (
            <div key={upload.id} className="relative group">
              <img 
                src={upload.file_url} 
                alt={upload.title}
                className="w-full h-32 object-cover rounded-lg border-2 border-slate-200"
                onError={(e) => { e.target.src = 'https://via.placeholder.com/200x150?text=File'; }}
              />
              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex flex-col items-center justify-center text-white p-2">
                <p className="font-bold text-xs text-center">{upload.title}</p>
                <Badge variant="default" className="mt-1 text-xs">{uploadTypeLabels[upload.upload_type]}</Badge>
                <p className="text-xs mt-1">{upload.uploaded_by_name}</p>
                {(upload.uploaded_by === user?.id || ['admin', 'office_staff'].includes(user?.role)) && (
                  <button
                    onClick={() => handleDelete(upload.id)}
                    className="absolute top-1 right-1 p-1 bg-red-600 rounded hover:bg-red-700"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                )}
              </div>
            </div>
          ))}
          {uploads.length === 0 && (
            <div className="col-span-full text-center text-slate-500 py-6">
              <ImageIcon className="w-10 h-10 mx-auto mb-2 text-slate-300" />
              <p className="text-sm">No uploads yet</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default JobUploadsSection;
